<template>
  <div>
    <PDFEditor
      :key="documentPreviewData"
      :opened.sync="pdfEditorOpened"
      :pdf-src="documentPreviewData"
      :path="documentPath"
      :file-name="documentName"
      :current-company-id="selectedCompanyId"
      :questionnaire-id="questionnaireId"
      @on-saved="$emit('on-pdf-saved')"
    />

    <v-dialog
      max-width="1000px"
      v-model="opened"
      persistent
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="
        $emit('update:opened', false);
        previewRotation = 0;
      "
    >
      <v-card>
        <v-card-title class="title">
          <v-layout v-if="renameFile" align-center>
            <span class="caption mr-2">New Name:</span>
            <v-text-field
              v-model="newName"
              hide-details
              solo
              class="default-input"
            />
            <span class="subheading">.{{ documentExtension }}</span>
            <v-tooltip bottom class="mx-2">
              <i
                class="material-icons-outlined success--text"
                style="font-size: 20px; cursor: pointer"
                @click="renameItem()"
                slot="activator"
              >
                check
              </i>
              <span>Save</span>
            </v-tooltip>
            <v-tooltip bottom class="ml-2">
              <i
                class="material-icons-outlined error--text"
                style="font-size: 20px; cursor: pointer"
                @click="renameFile = false"
                slot="activator"
              >
                cancel
              </i>
              <span>Cancel</span>
            </v-tooltip>
          </v-layout>

          <v-layout v-else>
            <span>{{ documentName }}</span>
            <v-tooltip v-if="!hideRenameButton" bottom class="ml-2">
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 20px; cursor: pointer"
                @click="startRenaming()"
                slot="activator"
              >
                edit
              </i>
              <span>Rename</span>
            </v-tooltip>
          </v-layout>

          <v-spacer />

          <v-btn
            v-if="!hideEditButton && documentName.toLowerCase().endsWith('pdf')"
            class="ma-0 mr-1 text-none"
            color="primary"
            @click="pdfEditorOpened = true"
          >
            Edit
          </v-btn>

          <v-tooltip bottom class="mr-1" v-if="!hideRotateButton">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="previewRotation += 90"
              slot="activator"
            >
              <v-icon size="25"> rotate_right </v-icon>
            </v-btn>
            <span>Rotate</span>
          </v-tooltip>
          <v-tooltip bottom class="mx-1" v-if="isAdvisor && !hideMoveButton">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="openCopyDialog()"
              slot="activator"
            >
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 25px"
              >
                drive_file_move
              </i>
            </v-btn>
            <span>Move File</span>
          </v-tooltip>
          <v-tooltip bottom class="mx-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="
                downloadDocumentByLink(
                  documentPreviewData,
                  getDocumentName(selectedDocumentLink),
                )
              "
              slot="activator"
            >
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 25px"
              >
                get_app
              </i>
            </v-btn>
            <span>Download to Computer</span>
          </v-tooltip>
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0"
            @click="
              $emit('update:opened', false);
              previewRotation = 0;
            "
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="documentPreviewData && previewLoaded">
          <preview-document
            :document-preview-data="documentPreviewData"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
            :hideRotateButton="hideRotateButton"
          />
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import PreviewDocument from '@/components/PreviewDocument';
import API from '@/api';
import get from 'lodash/get';
import PDFEditor from '@/components/dialogs/PDFEditor';

export default {
  name: 'PreviewDocumentDialog',

  components: {
    PreviewDocument,
    PDFEditor,
  },

  props: {
    opened: {
      type: Boolean,
    },

    selectedMessageId: {
      type: Number,
    },

    selectedCompanyId: {
      type: Number,
    },

    selectedDocumentLink: {
      type: String,
    },

    previewType: {
      type: String,
    },

    previewLoaded: {
      type: Boolean,
    },

    documentPreviewData: {
      type: String,
    },

    documentType: {
      type: String,
    },

    hideMoveButton: {
      type: Boolean,
    },

    hideRotateButton: {
      type: Boolean,
    },

    hideEditButton: {
      type: Boolean,
    },

    hideRenameButton: {
      type: Boolean,
    },

    questionnaireId: {
      type: Number,
    },
  },

  data() {
    return {
      renameFile: false,
      previewRotation: 0,
      documentExtension: '',
      newName: '',
      pdfEditorOpened: false,
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      isAdvisor: 'isAdvisor',
    }),

    ...mapState('companies', ['companies']),

    documentName() {
      return this.getDocumentName(this.selectedDocumentLink);
    },

    documentPath() {
      const path = this.selectedDocumentLink
        .split('/')
        .slice(this.selectedDocumentLink.startsWith('/') ? 3 : 2)
        .join('/');
      return '/' + path.substring(0, path.lastIndexOf('/'));
    },
  },

  methods: {
    getDocumentName(link) {
      return link.split('/')[link.split('/').length - 1];
    },

    async downloadDocumentByLink(url, name) {
      let response = await this.$http.get(url, { responseType: 'arraybuffer' });
      let blob = new Blob([response.data], {
        type: get(response.headers, 'content-type'),
      });
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = name;
      link.click();
    },

    startRenaming() {
      this.documentExtension = this.getDocumentName(this.selectedDocumentLink)
        .split('.')
        .pop();
      this.newName = this.getDocumentName(this.selectedDocumentLink).replace(
        `.${this.documentExtension}`,
        '',
      );
      this.renameFile = true;
    },

    async renameItem() {
      try {
        let response;
        if (this.selectedMessageId) {
          response = await API.conversation.renameItem({
            newName: this.newName.concat('.', this.documentExtension),
            messageId: this.selectedMessageId,
            link: this.selectedDocumentLink,
          });
        } else {
          response = await API.integration.renameItem({
            itemName: this.documentName,
            newName:
              this.documentType === 'folder'
                ? this.newName
                : this.newName.concat('.', this.documentExtension),
            path: this.documentPath,
            companyId: this.selectedCompanyId,
          });
        }

        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$emit('update:selectedDocumentLink', response.data);
          this.$store.dispatch('application/setSuccessNotification', {
            message: `You successfully renamed the document!`,
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem renaming the document.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.$emit('refresh-conversation');
        this.newName = '';
        this.renameFile = false;
        this.documentExtension = '';
      }
    },
  },
};
</script>

<style scoped lang="postcss"></style>
